import React, { type ReactNode, useMemo } from 'react';

import clsx from 'clsx';
import type flatpickr from 'flatpickr';
import Flatpickr, {
  default as DatePicker,
  type DateTimePickerProps,
} from 'react-flatpickr';

import Icons from './icons';

interface FlatPickerProps {
  title?: string;
  placeholder?: string;
  onChangeHandler: flatpickr.Options.Hook;
  dateFormat?: string;
  value?: string | Date | number;
  minDate?: true | Date;
  enableTime?: boolean;
  minTime?: string;
  maxTime?: string;
  icon?: keyof typeof Icons;
  className?: string;
  innerClassName?: string;
  titleClassName?: string;
  id?: string;
  /** @deprecated */
  isOnModal?: boolean;
}

type IFPParent = Pick<
  FlatPickerProps,
  'className' | 'title' | 'icon' | 'innerClassName' | 'titleClassName' | 'id'
> & {
  children: ReactNode;
};

function FPParent({
  title,
  children,
  className,
  innerClassName,
  titleClassName,
  icon = 'date',
  id,
}: IFPParent) {
  const Icon = useMemo(() => Icons[icon], [icon]);
  return (
    <div className={clsx('flex flex-col', className)}>
      {title && (
        <label
          htmlFor={id}
          className={titleClassName ?? 'text-gray-800 dark:text-white'}
        >
          {title}
        </label>
      )}
      <div
        className={clsx(
          `outline-brand flex min-h-[2.25rem] items-stretch rounded focus-within:outline
          focus-within:outline-1`,
          innerClassName
        )}
      >
        <div className="bg-brand flex w-9 items-center justify-center rounded-l">
          <Icon width="20" />
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * @deprecated Use `TimePicker` instead
 */
function FlatPicker({
  title,
  onChangeHandler,
  value,
  minDate,
  enableTime,
  placeholder = 'Seleccionar fecha',
  dateFormat = 'Y-m-d',
  minTime,
  maxTime,
  className,
  innerClassName = 'h-9',
  titleClassName,
  icon = 'date',
  id,
}: FlatPickerProps) {
  return (
    <FPParent
      className={className}
      innerClassName={innerClassName}
      titleClassName={titleClassName}
      icon={icon}
      title={title}
      id={id}
    >
      <Flatpickr
        data-enable-time
        options={{
          dateFormat,
          enableTime: enableTime,
          minDate: minDate === true ? Date.now() : minDate,
          maxTime: maxTime,
          minTime: minTime,
        }}
        className={clsx(
          'flatpickr-input',
          'text-gray-700 dark:text-gray-200',
          'w-full rounded-r border border-l-0 px-2 text-center focus:outline-none',
          // isOnModal
          `dark:border-zinc-500 dark:bg-gray-600 dark:disabled:border-gray-600
          dark:disabled:bg-gray-700`
          // : 'border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:disabled:bg-gray-600'
        )}
        value={value}
        onChange={onChangeHandler}
        placeholder={placeholder}
        id={id}
      />
    </FPParent>
  );
}

FlatPicker.displayName = 'FlatPicker';

interface ITimePicker extends DateTimePickerProps {
  label?: string;
  icon?: keyof typeof Icons;
  innerClassName?: string;
  titleClassName?: string;
  /** @deprecated */
  isOnModal?: boolean;
  inputClassName?: string;
}
const TimePicker = React.forwardRef<DatePicker, ITimePicker>(
  function TimePicker(
    {
      label,
      icon = 'date',
      className,
      innerClassName = 'h-9',
      titleClassName,
      isOnModal,
      inputClassName,
      ...fpProps
    },
    ref
  ) {
    return (
      <FPParent
        className={className}
        innerClassName={innerClassName}
        titleClassName={titleClassName}
        icon={icon}
        title={label}
        id={fpProps.id}
      >
        <Flatpickr
          className={clsx(
            'flatpickr-input',
            'text-gray-700 dark:text-gray-200',
            'w-full rounded-r border border-l-0 px-2 text-center focus:outline-none',
            // isOnModal
            // PWA style has changed enough that this class should apply to all
            `dark:border-zinc-500 dark:bg-gray-600 dark:disabled:border-gray-600
            dark:disabled:bg-gray-700`,
            inputClassName
          )}
          {...fpProps}
          options={{ minuteIncrement: 1, ...fpProps.options }}
          ref={ref}
        />
      </FPParent>
    );
  }
);

TimePicker.displayName = 'TimePicker';

export { FlatPicker, TimePicker };
export type { FlatPickerProps, ITimePicker };
